import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Spinner, Table } from 'react-bootstrap';
import axios, { apiBaseUrl } from '../api/httpClient';

interface Reconciliation {
  latestBatch: null | {
    id: string; sourceSnapshotAt: string; importedAt: string; actualImportActor: string; snapshotSha256: string;
    report: { sourceTotal: number; reconciliationClear: boolean; evidenceUrl: string };
  };
  retainedSourceRows: number;
  missingSourceRows: number;
  exceptions: { page: number; lastPage: number; total: number; items: { sourceKey: string; code: string }[] };
}

const reasons: Record<string, string> = {
  approval_history_not_reconciled: 'Source approval history not yet linked',
  review_history_not_reconciled: 'Source review history not yet linked',
  evidence_attachments_not_reconciled: 'Source evidence attachment not yet linked',
  native_changes_require_reconciliation: 'Native changes preserved; reconcile with source',
  concurrent_native_change_preserved: 'Concurrent native change preserved',
  source_missing_no_automatic_retirement: 'Absent from source; retained, not automatically retired',
  source_owner_unassigned: 'Source owner not assigned',
  policy_content_not_reconciled: 'Controlled policy content not yet linked',
  policy_document_not_reconciled: 'Policy document version or review schedule not yet linked',
};

const IntegratedManagementSourceReconciliation: React.FC = () => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Reconciliation | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const load = useCallback(async (active: () => boolean) => {
    setLoading(true); setError(false);
    try {
      const response = await axios.get<Reconciliation>(`${apiBaseUrl}/integrated-management-system/source-reconciliation`, { params: { page, perPage: 50 } });
      if (active()) setData(response.data);
    } catch {
      if (active()) { setData(null); setError(true); }
    } finally {
      if (active()) setLoading(false);
    }
  }, [page]);
  useEffect(() => {
    let active = true; load(() => active);
    return () => { active = false; };
  }, [load, refresh]);
  return <Card className="mb-4">
    <Card.Header className="d-flex justify-content-between align-items-center">
      <strong>Adoptech source reconciliation</strong>
      <Button size="sm" variant="outline-secondary" disabled={loading} onClick={() => setRefresh(refresh + 1)}>Refresh source reconciliation</Button>
    </Card.Header>
    <Card.Body>
      <Alert variant="warning">Cutover is not authorised by this report. This shows the last applied snapshot, not live Adoptech parity. Historical source decisions are separate from native reviews and approvals.</Alert>
      {loading && <div><Spinner size="sm" animation="border" /> Loading source reconciliation…</div>}
      {error && <Alert variant="danger">Source reconciliation could not be loaded. No migration or readiness conclusion can be drawn.</Alert>}
      {!loading && data && !data.latestBatch && <p className="mb-0">No operational source import has been applied. The archived overview remains separate from native IMS records.</p>}
      {!loading && data?.latestBatch && <>
        <p><Badge bg={data.exceptions.total ? 'warning' : 'secondary'}>{data.exceptions.total} reconciliation flags</Badge> · {data.latestBatch.report.sourceTotal} records in the applied snapshot · {data.retainedSourceRows} source rows retained</p>
        <dl className="row small">
          <dt className="col-sm-3">Source snapshot</dt><dd className="col-sm-9">{data.latestBatch.sourceSnapshotAt}</dd>
          <dt className="col-sm-3">Imported</dt><dd className="col-sm-9">{data.latestBatch.importedAt}</dd>
          <dt className="col-sm-3">Actual import actor</dt><dd className="col-sm-9">{data.latestBatch.actualImportActor}</dd>
          <dt className="col-sm-3">Snapshot SHA-256</dt><dd className="col-sm-9 text-break">{data.latestBatch.snapshotSha256}</dd>
        </dl>
        <p className="small text-muted">History/evidence flags describe export provenance gaps; they are not automatically failed controls. Missing source rows are retained without automatic deletion or retirement.</p>
        {data.exceptions.total === 0 ? <p>No flags in the last import. Backup/restore, access review, current-source parity and management cutover checks are still required.</p> : <>
          <Table responsive size="sm"><thead><tr><th>Source identity</th><th>Reconciliation needed</th></tr></thead>
            <tbody>{data.exceptions.items.map((item, index) => <tr key={`${item.sourceKey}:${item.code}:${index}`}><td>{item.sourceKey}</td><td>{reasons[item.code] || item.code}</td></tr>)}</tbody>
          </Table>
          {data.exceptions.lastPage > 1 && <div className="d-flex justify-content-between align-items-center">
            <Button size="sm" variant="outline-secondary" disabled={loading || page <= 1} onClick={() => setPage(page - 1)}>Previous reconciliation page</Button>
            <span>Reconciliation page {page} of {data.exceptions.lastPage}</span>
            <Button size="sm" variant="outline-secondary" disabled={loading || page >= data.exceptions.lastPage} onClick={() => setPage(page + 1)}>Next reconciliation page</Button>
          </div>}
        </>}
      </>}
    </Card.Body>
  </Card>;
};

export default IntegratedManagementSourceReconciliation;
